/**
 * Pure layout helpers for Spin the Wheel segment image/text regions.
 * Does not affect selection angles or spin planning.
 */

export const WHEEL_VIEWBOX_SIZE = 400;
export const WHEEL_CX = WHEEL_VIEWBOX_SIZE / 2;
export const WHEEL_CY = WHEEL_VIEWBOX_SIZE / 2;
export const WHEEL_RADIUS = 188;
export const WHEEL_HUB_RADIUS = 46;

/** Outer rim of the legacy image band (unused by text-only wheel). */
export const WHEEL_IMAGE_OUTER_R = WHEEL_RADIUS * 0.97;
/** Inner rim of the legacy image band (unused by text-only wheel). */
export const WHEEL_IMAGE_INNER_R = WHEEL_RADIUS * 0.8;

/**
 * Outer rim of the radial text band — near the wheel edge now that segments
 * are text-only (padding from the rim).
 */
export const WHEEL_TEXT_OUTER_R = WHEEL_RADIUS * 0.92;
/** Inner rim of the text band (clear of the hub / spin control). */
export const WHEEL_TEXT_INNER_R = WHEEL_HUB_RADIUS + 16;

/** Absolute cap so package art stays recognisable but compact. */
export const WHEEL_IMAGE_MAX_SIZE = WHEEL_RADIUS * 0.2;

/**
 * Below this viewBox size, images are suppressed (identifiers/colour only)
 * rather than rendered oversized and clipped by the wedge.
 */
export const WHEEL_IMAGE_MIN_RENDER_SIZE = 6;

export type WheelImageSlot = {
  /** Centre of the contained image box. */
  cx: number;
  cy: number;
  /** Square box size used with preserveAspectRatio meet (contain). */
  size: number;
  outerR: number;
  innerR: number;
  midR: number;
  /** False when the wedge is too narrow for an unclipped contained image. */
  showImage: boolean;
};

export type WheelRadialLabelPose = {
  x: number;
  y: number;
  /** SVG rotate degrees around (x, y); baseline along the segment radius. */
  rotateDeg: number;
  /** True when lower-half flip was applied so glyphs stay upright. */
  flipped: boolean;
};

function polar(
  cx: number,
  cy: number,
  r: number,
  angleFromTopCw: number,
): { x: number; y: number } {
  const rad = ((-90 + angleFromTopCw) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

/**
 * Contained image slot in the outer annular wedge of a segment.
 * Sized to fit inside the band and the wedge angle (axis-aligned square),
 * without a floor that would force clipping on dense catalogues.
 */
export function wheelImageSlot(
  midDeg: number,
  sweepDeg: number,
  options: {
    padding?: number;
    maxSize?: number;
    minRenderSize?: number;
  } = {},
): WheelImageSlot {
  const padding = options.padding ?? 0.62;
  const maxSize = options.maxSize ?? WHEEL_IMAGE_MAX_SIZE;
  const minRenderSize = options.minRenderSize ?? WHEEL_IMAGE_MIN_RENDER_SIZE;
  const outerR = WHEEL_IMAGE_OUTER_R;
  const innerR = WHEEL_IMAGE_INNER_R;
  const midR = (outerR + innerR) / 2;
  const band = outerR - innerR;
  // Clamp half-angle to 90°: sin(180°)=0 would incorrectly zero a full-circle slot.
  const halfSweepDeg = Math.min(Math.max(sweepDeg, 0) / 2, 90);
  const halfSweepRad = (halfSweepDeg * Math.PI) / 180;
  // Lateral room to the wedge edge at midR, then shrink for square diagonal.
  const maxHalfExtent = midR * Math.sin(halfSweepRad) * padding;
  const maxByAngle = (2 * maxHalfExtent) / Math.SQRT2;
  const maxByBand = band * padding;
  const raw = Math.min(maxByAngle, maxByBand, maxSize);
  const showImage = raw >= minRenderSize;
  const size = showImage ? raw : 0;
  const pos = polar(WHEEL_CX, WHEEL_CY, midR, midDeg);
  return {
    cx: pos.x,
    cy: pos.y,
    size,
    outerR,
    innerR,
    midR,
    showImage,
  };
}

/**
 * Angular half-width (degrees) from the segment mid-line to a corner of the
 * axis-aligned image square. Must stay ≤ sweep/2 for unclipped containment.
 */
export function wheelImageCornerAngleHalfDeg(slot: WheelImageSlot): number {
  if (slot.size <= 0 || slot.midR <= 0) return 0;
  const halfDiag = (slot.size * Math.SQRT2) / 2;
  return (Math.asin(Math.min(1, halfDiag / slot.midR)) * 180) / Math.PI;
}

/**
 * True when a rendered image square stays inside the wedge angle (no clip).
 * Suppressed slots (showImage false) are treated as fitting.
 */
export function wheelImageFitsWedge(
  slot: WheelImageSlot,
  sweepDeg: number,
): boolean {
  if (!slot.showImage || slot.size <= 0) return true;
  return wheelImageCornerAngleHalfDeg(slot) <= sweepDeg / 2 + 1e-6;
}

/**
 * True when the image box stays outside the hub radius (with a small margin).
 */
export function wheelImageClearsHub(
  slot: WheelImageSlot,
  hubRadius: number = WHEEL_HUB_RADIUS,
): boolean {
  if (!slot.showImage || slot.size <= 0) return true;
  const dist = Math.hypot(slot.cx - WHEEL_CX, slot.cy - WHEEL_CY);
  return dist - slot.size / 2 >= hubRadius + 4;
}

/**
 * True when the image box stays within the outer image radial band.
 */
export function wheelImageInsideOuterBand(slot: WheelImageSlot): boolean {
  if (!slot.showImage || slot.size <= 0) return true;
  const dist = Math.hypot(slot.cx - WHEEL_CX, slot.cy - WHEEL_CY);
  const half = slot.size / 2;
  return dist + half <= slot.outerR + 0.5 && dist - half >= slot.innerR - 0.5;
}

/**
 * Angular half-width from segment mid to either edge (degrees).
 * Used to assert neighbouring labels do not share the same mid angle.
 */
export function wheelSegmentHalfSweep(segmentCount: number): number {
  if (segmentCount <= 0) return 0;
  return 180 / segmentCount;
}

/**
 * Radial label pose: baseline along the segment centre line, reading from the
 * wide outer end toward the hub. Lower-half segments flip 180° so glyphs stay
 * upright.
 */
export function wheelRadialLabelPose(
  midDeg: number,
  options: { radius?: number } = {},
): WheelRadialLabelPose {
  const radius =
    options.radius ?? (WHEEL_TEXT_OUTER_R + WHEEL_TEXT_INNER_R) / 2;
  const pos = polar(WHEEL_CX, WHEEL_CY, radius, midDeg);
  // Inward along radius: at top (midDeg=0) text reads downward toward centre.
  let rotateDeg = midDeg + 90;
  let flipped = false;
  if (midDeg > 90 && midDeg < 270) {
    rotateDeg += 180;
    flipped = true;
  }
  return {
    x: pos.x,
    y: pos.y,
    rotateDeg,
    flipped,
  };
}

/**
 * True when the label anchor stays clear of the hub / spin control.
 */
export function wheelLabelClearsHub(
  pose: WheelRadialLabelPose,
  hubRadius: number = WHEEL_HUB_RADIUS,
): boolean {
  const dist = Math.hypot(pose.x - WHEEL_CX, pose.y - WHEEL_CY);
  return dist >= hubRadius + 10;
}

/**
 * Mobile shows Product Name radial text in full/short modes (text-only slices).
 */
export function wheelMobileShowsRadialText(labelMode: string): boolean {
  return labelMode === 'full' || labelMode === 'short';
}